import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { KeyRound, Eye, EyeOff, Loader2, ArrowRight, Check, AlertCircle } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Token mancante nel link.");
      return;
    }
    if (password.length < 8) {
      setError("La password deve avere almeno 8 caratteri.");
      return;
    }
    if (password !== confirm) {
      setError("Le due password non coincidono.");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate("/auth", { replace: true }), 2500);
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          err.message ||
          "Errore."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-20 max-w-xl mx-auto" data-testid="reset-password-page">
      <PageHeader
        eyebrow="Reimposta password"
        title="Scegli una nuova password"
        description="Inserisci la nuova password che vuoi usare per accedere a TIME Inside."
        testId="reset-header"
      />
      <div className="glass-card p-6 sm:p-8">
        {success ? (
          <div className="text-center py-4" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center mx-auto mb-6">
              <Check className="w-7 h-7 text-emerald-300" />
            </div>
            <p className="text-sm text-white/70 font-light leading-relaxed mb-6">
              Password aggiornata con successo. Ti stiamo reindirizzando al login…
            </p>
            <Link
              to="/auth"
              data-testid="reset-goto-login"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-400/90 text-black text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition-all"
            >
              Vai al login
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
            {[
              { id: "pwd", label: "Nuova password", val: password, set: setPassword, tid: "reset-input-password" },
              { id: "cfm", label: "Conferma password", val: confirm, set: setConfirm, tid: "reset-input-confirm" },
            ].map((f) => (
              <label key={f.id} className="block">
                <span className="block text-[11px] tracking-[0.24em] uppercase text-white/40 mb-2">
                  {f.label}
                </span>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    data-testid={f.tid}
                    type={visible ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={8}
                    value={f.val}
                    onChange={(e) => f.set(e.target.value)}
                    className="w-full pl-10 pr-11 py-3 rounded-xl bg-black/40 border border-white/10 text-sm text-white placeholder-white/25 focus:border-emerald-400/40 focus:outline-none transition-colors"
                    placeholder="Almeno 8 caratteri"
                  />
                  <button
                    type="button"
                    onClick={() => setVisible((v) => !v)}
                    data-testid={`${f.tid}-toggle`}
                    aria-label={visible ? "Nascondi password" : "Mostra password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                  >
                    {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </label>
            ))}
            {error && (
              <div
                data-testid="reset-error"
                className="flex items-start gap-2 text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-4 py-3"
              >
                <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-400/90 text-black text-[12px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition-all disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Aggiorna password
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
